/**
 * Normalize + validate a user-entered URL from the link / image popover.
 *
 * Behavior:
 *   - Empty / whitespace-only → null (caller renders a "required" hint).
 *   - Bare hostname like `example.com` → auto-prepend `https://`.
 *   - `http://` or `https://` prefix → keep verbatim.
 *   - Any other scheme (`ftp://`, `mailto:`, `javascript:`, etc.) → null
 *     to keep the editor safe + the round-trip predictable. Markdown
 *     [text](url) is what gets serialized, and rendered consumers will
 *     trust whatever scheme lands — restricting to http(s) at input is
 *     the cheapest sanitization.
 *   - Anything that fails `new URL(...)` → null.
 */
export function normalizeLinkUrl(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === '') return null

  const tryParse = (s: string): URL | null => {
    try {
      return new URL(s)
    } catch {
      return null
    }
  }

  // Try parsing as-is first. Any string with a scheme (`mailto:user`,
  // `javascript:alert(1)`, `ftp://x`) parses successfully and we can
  // gate on the protocol — we reject anything non-http(s) here so
  // `mailto:` and friends don't sneak past the auto-prepend path below.
  const direct = tryParse(trimmed)
  if (direct) {
    return direct.protocol === 'http:' || direct.protocol === 'https:' ? direct.toString() : null
  }

  // No scheme detected — auto-prepend `https://` and try again. This
  // is the `example.com` → `https://example.com/` ergonomic path.
  const prepended = tryParse(`https://${trimmed}`)
  if (prepended && (prepended.protocol === 'http:' || prepended.protocol === 'https:')) {
    return prepended.toString()
  }
  return null
}
